import { StatusBar, StatusBarStyle } from 'expo-status-bar'
import React, { useState, useEffect, useCallback } from 'react'
import { StyleSheet, Platform } from 'react-native'
import { SafeAreaView } from 'react-native-safe-area-context'

import { useStore } from '../../models'
import { DS } from '../../design-system'
import { Images, WhereAreYouChoice, WhereAreYouChoiceSkeleton } from './WhereAreYouChoice'
import { Palette } from '@ppwr/poker'
import { events } from '../../models/analytics/analytics-events'
import { translate } from '../../i18n'
import { EventType } from './name-screen.interface'
import { observer } from 'mobx-react-lite'
import { useRouter } from 'expo-router'
import { defaultBreakPoints, isWeb } from '@/components/WebWrapper'

interface EventChoice {
  image: keyof typeof Images
  text: string
  event: EventType
  eventToTrack: string
}

const eventsList: EventChoice[] = [
  {
    image: 'InPersonImage',
    text: 'In Person Event',
    eventToTrack: 'In Person Event',
    event: EventType.InPerson
  },
  {
    image: 'VirtualEvent',
    text: 'Virtual Event',
    eventToTrack: 'Virtual Event',
    event: EventType.VirtualEvent
  },
  {
    image: 'Class',
    text: 'Class',
    eventToTrack: 'Class',
    event: EventType.Class
  },
  {
    image: 'NoneAbove',
    text: 'None of the above',
    eventToTrack: 'N/A',
    event: EventType.NoneAbove
  }
]

export const WhereAreYouScreen = observer(() => {
  const router = useRouter()
  const store = useStore()
  const [isLoading, setIsLoading] = useState(true)
  const getInPersonEvents = store.monday.getInPersonEvents()
  const getVirtualEvents = store.monday.getVirtualEvents()

  const trackEvent = (event: EventType) => {
    store.analytics.setEvent(events.onboarding.where_are_you_form_completed, {
      eventName: eventsList.find((e) => e.event === event)?.eventToTrack,
      segmentUserId: store.analytics.getSegmentUserID(),
      segmentIdentity: store.analytics.getUserIdentify()
    })
  }

  const handleEventChoice = (event: EventType) => {
    trackEvent(event)
    switch (event) {
      case EventType.InPerson:
        router.push(`/onboarding/which-event?eventType=${EventType.InPerson}`)
        break
      case EventType.VirtualEvent:
        router.push(`/onboarding/which-event?eventType=${EventType.VirtualEvent}`)
        break
      case EventType.Class:
        router.push('/onboarding/which-class-type')
        break
      case EventType.NoneAbove:
        router.push('/onboarding/name')
        break
    }
  }

  const filteredEvents = eventsList.filter((event) => {
    if (event.event === EventType.InPerson) {
      return getInPersonEvents.length > 0
    }
    if (event.event === EventType.VirtualEvent) {
      return getVirtualEvents.length > 0
    }
    return true
  })

  const fetchEvents = store.monday.fetchMondayEvents

  const callApi = useCallback(async () => {
    setIsLoading(true)
    try {
      await fetchEvents()
      setIsLoading(false)
    } catch (error) {
      console.error('callApi.fetchEvents:', error)
    } finally {
      setIsLoading(false)
    }
  }, [fetchEvents])

  useEffect(() => {
    callApi()
  }, [callApi])

  return (
    <SafeAreaView style={{ backgroundColor: Palette['Brand/Cream'], flexGrow: 1 }}>
      <StatusBar animated={true} style={'dark' as StatusBarStyle} hidden={false} />
      <DS.Frame style={styles.container}>
        <DS.Text variant="Heading 1/Medium" textAlign="center">
          {translate('whereAreYouScreen.title')}
        </DS.Text>

        <DS.Frame style={styles.options}>
          {isLoading && Array.from({ length: 3 }).map((_, i) => <WhereAreYouChoiceSkeleton key={`skeleton-${i}`} />)}

          {!isLoading &&
            filteredEvents.map((event, index) => (
              <WhereAreYouChoice
                key={index}
                image={event.image}
                text={event.text}
                onPress={() => handleEventChoice(event.event)}
              />
            ))}
        </DS.Frame>
      </DS.Frame>
    </SafeAreaView>
  )
})

export const styles = StyleSheet.create({
  container: {
    maxWidth: isWeb ? defaultBreakPoints.sm : undefined,
    marginHorizontal: isWeb ? 'auto' : undefined,
    paddingTop: Platform.OS === 'web' ? 24 : 0,
    paddingHorizontal: 24,
    justifyContent: !isWeb ? 'center' : undefined,
    height: '100%',
    gap: 32
  },
  options: {
    gap: 8
  }
})
